import { useState } from 'react';
import { User, Mail, Shield, Edit, Camera, Lock, ArrowLeft, Phone, Save, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function AdminProfile() {
  const { user, setUser, changePassword } = useAuth();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const API = `${BACKEND_URL}/api`;

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const updatedUser = await response.json();
      setUser(prev => ({ ...prev, ...formData }));
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return toast.error('Passwords do not match');
    }
    
    setUpdating(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully');
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="admin-profile">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl text-secondary">My Profile</h1>
          <p className="text-muted-foreground font-body mt-1">Manage your administrator account details</p>
        </div>
        {!isEditingProfile && !isChangingPassword && (
          <Button variant="outline" className="flex items-center gap-2" onClick={() => setIsEditingProfile(true)}>
            <Edit className="w-4 h-4" />
            Edit Profile
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Avatar & Basic Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-soft flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="w-24 h-24 rounded-full border-4 border-white shadow-sm overflow-hidden bg-primary/10 flex items-center justify-center">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-full h-full object-cover" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'Admin')}&background=E0E7FF&color=0071E3&size=96`;
                    }}
                  />
                ) : (
                  <span className="text-3xl font-bold text-primary">{user.name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <button className="absolute bottom-0 right-0 p-1.5 bg-white rounded-full shadow-md border border-gray-100 hover:bg-surface transition-colors">
                <Camera className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <h2 className="text-xl font-heading text-secondary">{user.name}</h2>
            <Badge className="mt-2 bg-primary/10 text-primary border-none font-medium capitalize">
              System Administrator
            </Badge>

            <div className="w-full mt-6 pt-6 border-t border-gray-100 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground font-body mb-1">Status</p>
                <div className="flex items-center justify-center gap-1.5">
                  <div className="w-2 h-2 bg-success rounded-full" />
                  <span className="text-sm font-medium text-success">Active</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-body mb-1">Role</p>
                <p className="text-sm font-medium text-secondary">Full Admin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Detailed Info & Security */}
        <div className="lg:col-span-2 space-y-6">
          {isChangingPassword ? (
            /* Change Password Card */
            <div className="bg-white rounded-2xl p-6 shadow-soft animate-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-4 mb-8">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsChangingPassword(false)}
                  disabled={updating}
                  className="rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <h3 className="font-heading text-lg text-secondary">Change Password</h3>
              </div>

              <form className="space-y-6 max-w-md mx-auto py-4" onSubmit={handlePasswordUpdate}>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-black text-muted-foreground ml-1">Current Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      type="password" 
                      required
                      placeholder="Enter current password"
                      className="pl-10 rounded-xl bg-surface border-transparent focus:bg-white transition-all"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-black text-muted-foreground ml-1">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      type="password" 
                      required
                      minLength={8}
                      placeholder="Min. 8 characters"
                      className="pl-10 rounded-xl bg-surface border-transparent focus:bg-white transition-all"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-black text-muted-foreground ml-1">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      type="password" 
                      required
                      placeholder="Repeat new password"
                      className="pl-10 rounded-xl bg-surface border-transparent focus:bg-white transition-all"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    />
                  </div>
                </div>

                <div className="pt-4 flex flex-col gap-3">
                  <Button type="submit" disabled={updating} className="w-full rounded-xl py-6 font-bold shadow-lg shadow-primary/20">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Update Password
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    disabled={updating}
                    className="w-full text-muted-foreground hover:text-secondary"
                    onClick={() => setIsChangingPassword(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          ) : isEditingProfile ? (
            /* Edit Profile Card */
            <div className="bg-white rounded-2xl p-6 shadow-soft animate-in slide-in-from-right-4 duration-300">
               <div className="flex items-center gap-4 mb-8">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsEditingProfile(false)}
                  disabled={updating}
                  className="rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <h3 className="font-heading text-lg text-secondary">Edit Account Details</h3>
              </div>

              <form className="space-y-6 max-w-md mx-auto py-4" onSubmit={handleUpdateProfile}>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-black text-muted-foreground ml-1">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      required
                      placeholder="Your full name"
                      className="pl-10 rounded-xl bg-surface border-transparent focus:bg-white transition-all"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-black text-muted-foreground ml-1">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      type="email" 
                      required
                      placeholder="admin@example.com"
                      className="pl-10 rounded-xl bg-surface border-transparent focus:bg-white transition-all"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-black text-muted-foreground ml-1">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="e.g. 9876543210"
                      className="pl-10 rounded-xl bg-surface border-transparent focus:bg-white transition-all"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="pt-4 flex flex-col gap-3">
                  <Button type="submit" disabled={updating} className="w-full rounded-xl py-6 font-bold shadow-lg shadow-primary/20 bg-primary">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    disabled={updating}
                    className="w-full text-muted-foreground hover:text-secondary"
                    onClick={() => setIsEditingProfile(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <>
              {/* Account Details Card */}
              <div className="bg-white rounded-2xl p-6 shadow-soft animate-in fade-in duration-500">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-lg text-secondary">Account Details</h3>
                  <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80" onClick={() => setIsEditingProfile(true)}>
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-body flex items-center gap-2">
                      <User className="w-3.5 h-3.5" />
                      Full Name
                    </label>
                    <p className="font-body text-secondary p-3 bg-surface rounded-xl border border-transparent">
                      {user.name}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-body flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" />
                      Email Address
                    </label>
                    <p className="font-body text-secondary p-3 bg-surface rounded-xl border border-transparent truncate">
                      {user.email}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-body flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" />
                      Phone Number
                    </label>
                    <p className="font-body text-secondary p-3 bg-surface rounded-xl border border-transparent">
                      {user.phone || 'Not provided'}
                    </p>
                  </div>

                </div>
              </div>

              {/* Security Card */}
              <div className="bg-white rounded-2xl p-6 shadow-soft transition-all overflow-hidden relative group">
                <h3 className="font-heading text-lg text-secondary mb-6">Security & Preferences</h3>
                <div className="max-w-xs">
                  <Button 
                    variant="outline" 
                    onClick={() => setIsChangingPassword(true)}
                    className="w-full h-auto py-6 flex flex-col gap-2 rounded-xl border-gray-100 hover:bg-surface hover:border-primary/20 transition-all group"
                  >
                    <Shield className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="font-medium">Change Password</span>
                  </Button>
                </div>
                
                {/* Visual Flair */}
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
